import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Collapse,
  Divider,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add,
  Edit,
  Delete,
  CheckCircle,
  HourglassEmpty,
  Phone,
  Email,
  ExpandMore,
  ExpandLess,
  Warning,
} from '@mui/icons-material';
import { referencesApi, CandidateReference, ReferenceCheck, ReferenceSummary } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface CandidateReferencesProps {
  candidateId: string;
}

const relationshipLabels: Record<string, string> = {
  supervisor: 'Supervisor',
  coworker: 'Coworker',
  hr: 'HR Department',
  other: 'Other',
};

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  pending: 'default',
  contacted: 'primary',
  completed: 'success',
  unable_to_reach: 'warning',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  contacted: 'Contacted',
  completed: 'Completed',
  unable_to_reach: 'Unable to Reach',
};

const CandidateReferences: React.FC<CandidateReferencesProps> = ({ candidateId }) => {
  const { user } = useAuth();
  const canEdit = user?.role && ['admin', 'hr', 'manager'].includes(user.role);

  const [references, setReferences] = useState<CandidateReference[]>([]);
  const [summary, setSummary] = useState<ReferenceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [checkDialogOpen, setCheckDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedReference, setSelectedReference] = useState<CandidateReference | null>(null);
  const [expandedReference, setExpandedReference] = useState<number | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    title: '',
    relationship: 'supervisor' as const,
    phone: '',
    email: '',
  });

  const [checkFormData, setCheckFormData] = useState<Partial<ReferenceCheck>>({
    contact_method: 'phone',
    eligible_for_rehire: 'yes',
    is_draft: false,
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchReferences();
  }, [candidateId]);

  const fetchReferences = async () => {
    try {
      setLoading(true);
      const [refsResponse, summaryResponse] = await Promise.all([
        referencesApi.getReferences(candidateId),
        referencesApi.getSummary(candidateId),
      ]);
      setReferences(refsResponse.data);
      setSummary(summaryResponse.data);
      setError(null);
    } catch (err) {
      setError('Failed to load references');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReference = async () => {
    try {
      setSaving(true);
      await referencesApi.createReference(candidateId, formData);
      setAddDialogOpen(false);
      setFormData({ name: '', company: '', title: '', relationship: 'supervisor', phone: '', email: '' });
      fetchReferences();
    } catch (err) {
      setError('Failed to add reference');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReference = async () => {
    if (!selectedReference) return;
    try {
      setSaving(true);
      await referencesApi.deleteReference(selectedReference.id);
      setDeleteDialogOpen(false);
      setSelectedReference(null);
      fetchReferences();
    } catch (err) {
      setError('Failed to delete reference');
    } finally {
      setSaving(false);
    }
  };

  const handleStartCheck = (reference: CandidateReference) => {
    setSelectedReference(reference);
    if (reference.check) {
      setCheckFormData(reference.check);
    } else {
      setCheckFormData({
        contact_method: 'phone',
        eligible_for_rehire: 'yes',
        is_draft: false,
      });
    }
    setCheckDialogOpen(true);
  };

  const handleSaveCheck = async (isDraft: boolean) => {
    if (!selectedReference) return;
    try {
      setSaving(true);
      await referencesApi.saveCheck(selectedReference.id, {
        ...checkFormData,
        eligible_for_rehire: checkFormData.eligible_for_rehire || 'yes',
        is_draft: isDraft,
      });
      setCheckDialogOpen(false);
      setSelectedReference(null);
      fetchReferences();
    } catch (err) {
      setError('Failed to save reference check');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedReference(expandedReference === id ? null : id);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Panel */}
      {summary && summary.total > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Reference Check Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="h4">{summary.completed}/{summary.total}</Typography>
              <Typography variant="caption" color="text.secondary">Completed</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box>
                <Typography variant="body2">
                  Eligible for Rehire:
                  {summary.rehireStats.yes > 0 && <Chip size="small" label={`${summary.rehireStats.yes} Yes`} color="success" sx={{ ml: 1 }} />}
                  {summary.rehireStats.no > 0 && <Chip size="small" label={`${summary.rehireStats.no} No`} color="error" sx={{ ml: 1 }} />}
                  {summary.rehireStats.with_conditions > 0 && <Chip size="small" label={`${summary.rehireStats.with_conditions} Conditional`} color="warning" sx={{ ml: 1 }} />}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2">
                Overall: <Chip
                  size="small"
                  label={summary.overallSentiment}
                  color={summary.overallSentiment === 'positive' ? 'success' : summary.overallSentiment === 'negative' ? 'error' : 'default'}
                />
              </Typography>
            </Grid>
            {summary.hasDiscrepancies && (
              <Grid size={{ xs: 6, sm: 3 }}>
                <Tooltip title="Employment dates or titles don't match resume">
                  <Chip icon={<Warning />} label="Resume Mismatch" color="warning" size="small" />
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Header with Add button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          References ({references.length})
        </Typography>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Reference
          </Button>
        )}
      </Box>

      {/* References List */}
      {references.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No references added yet.
          </Typography>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
              sx={{ mt: 2 }}
            >
              Add First Reference
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={40}></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Company / Title</TableCell>
                <TableCell>Relationship</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rehire Eligible</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {references.map((ref) => (
                <React.Fragment key={ref.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleExpand(ref.id)}>
                        {expandedReference === ref.id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{ref.name}</Typography>
                      <Box display="flex" gap={1} mt={0.5}>
                        {ref.phone && (
                          <Tooltip title={ref.phone}>
                            <Phone fontSize="small" color="action" />
                          </Tooltip>
                        )}
                        {ref.email && (
                          <Tooltip title={ref.email}>
                            <Email fontSize="small" color="action" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {ref.company && <Typography variant="body2">{ref.company}</Typography>}
                      {ref.title && <Typography variant="caption" color="text.secondary">{ref.title}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={relationshipLabels[ref.relationship]} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={ref.status === 'completed' ? <CheckCircle /> : <HourglassEmpty />}
                        label={statusLabels[ref.status]}
                        color={statusColors[ref.status]}
                      />
                    </TableCell>
                    <TableCell>
                      {ref.check && !ref.check.is_draft ? (
                        <Chip
                          size="small"
                          label={ref.check.eligible_for_rehire === 'yes' ? 'Yes' :
                                 ref.check.eligible_for_rehire === 'no' ? 'No' :
                                 ref.check.eligible_for_rehire === 'with_conditions' ? 'Conditional' : 'N/A'}
                          color={ref.check.eligible_for_rehire === 'yes' ? 'success' :
                                 ref.check.eligible_for_rehire === 'no' ? 'error' :
                                 ref.check.eligible_for_rehire === 'with_conditions' ? 'warning' : 'default'}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {canEdit && (
                        <>
                          <Button
                            size="small"
                            onClick={() => handleStartCheck(ref)}
                          >
                            {ref.status === 'completed' ? 'View' : 'Check'}
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedReference(ref);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedReference === ref.id ? undefined : 'none' }}>
                      <Collapse in={expandedReference === ref.id}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          {ref.check && !ref.check.is_draft ? (
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="caption" color="text.secondary">Employment Dates</Typography>
                                <Typography variant="body2">{ref.check.dates_employed || '—'}</Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="caption" color="text.secondary">Title Held</Typography>
                                <Typography variant="body2">{ref.check.title_held || '—'}</Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="caption" color="text.secondary">Reason for Leaving</Typography>
                                <Typography variant="body2">{ref.check.reason_for_leaving || '—'}</Typography>
                              </Grid>
                              {ref.check.matches_resume && ref.check.matches_resume !== 'yes' && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Alert severity="warning" icon={<Warning />}>
                                    <Typography variant="caption">Resume Mismatch</Typography>
                                    <Typography variant="body2">
                                      Employment details {ref.check.matches_resume === 'no' ? 'do not match' : 'partially match'} resume
                                    </Typography>
                                  </Alert>
                                </Grid>
                              )}
                              <Grid size={12}>
                                <Typography variant="caption" color="text.secondary">Strengths</Typography>
                                <Typography variant="body2">{ref.check.strengths || '—'}</Typography>
                              </Grid>
                              <Grid size={12}>
                                <Typography variant="caption" color="text.secondary">Areas for Improvement</Typography>
                                <Typography variant="body2">{ref.check.areas_for_improvement || '—'}</Typography>
                              </Grid>
                              {ref.check.concerns && (
                                <Grid size={12}>
                                  <Alert severity="warning" icon={<Warning />}>
                                    <Typography variant="caption">Concerns</Typography>
                                    <Typography variant="body2">{ref.check.concerns}</Typography>
                                  </Alert>
                                </Grid>
                              )}
                              <Grid size={12}>
                                <Typography variant="caption" color="text.secondary">
                                  Checked by {ref.check.checked_by_first_name} {ref.check.checked_by_last_name} on {new Date(ref.check.checked_at || '').toLocaleDateString()}
                                </Typography>
                              </Grid>
                            </Grid>
                          ) : (
                            <Typography color="text.secondary">Reference check not completed yet.</Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Reference Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Reference</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Relationship</InputLabel>
                <Select
                  value={formData.relationship}
                  label="Relationship"
                  onChange={(e) => setFormData({ ...formData, relationship: e.target.value as any })}
                >
                  <MenuItem value="supervisor">Supervisor</MenuItem>
                  <MenuItem value="coworker">Coworker</MenuItem>
                  <MenuItem value="hr">HR Department</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddReference} variant="contained" disabled={!formData.name || saving}>
            {saving ? <CircularProgress size={20} /> : 'Add Reference'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reference Check Dialog */}
      <Dialog open={checkDialogOpen} onClose={() => setCheckDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Reference Check: {selectedReference?.name}
          <Typography variant="caption" display="block" color="text.secondary">
            {selectedReference?.company} - {selectedReference?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Contact Method */}
            <Grid size={12}>
              <FormControl component="fieldset">
                <FormLabel>Contact Method</FormLabel>
                <RadioGroup
                  row
                  value={checkFormData.contact_method || 'phone'}
                  onChange={(e) => setCheckFormData({ ...checkFormData, contact_method: e.target.value as any })}
                >
                  <FormControlLabel value="phone" control={<Radio />} label="Phone" />
                  <FormControlLabel value="email" control={<Radio />} label="Email" />
                  <FormControlLabel value="in_person" control={<Radio />} label="In Person" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <Divider><Typography variant="caption">Employment Verification</Typography></Divider>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Dates Employed"
                placeholder="e.g., Jan 2020 - Mar 2023"
                value={checkFormData.dates_employed || ''}
                onChange={(e) => setCheckFormData({ ...checkFormData, dates_employed: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Title Held"
                value={checkFormData.title_held || ''}
                onChange={(e) => setCheckFormData({ ...checkFormData, title_held: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Matches Resume?</InputLabel>
                <Select
                  value={checkFormData.matches_resume || ''}
                  label="Matches Resume?"
                  onChange={(e) => setCheckFormData({ ...checkFormData, matches_resume: e.target.value as any })}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Reason for Leaving"
                value={checkFormData.reason_for_leaving || ''}
                onChange={(e) => setCheckFormData({ ...checkFormData, reason_for_leaving: e.target.value })}
              />
            </Grid>

            <Grid size={12}>
              <Divider><Typography variant="caption">Rehire Eligibility</Typography></Divider>
            </Grid>

            <Grid size={12}>
              <FormControl component="fieldset" required>
                <FormLabel>Eligible for Rehire?</FormLabel>
                <RadioGroup
                  row
                  value={checkFormData.eligible_for_rehire || 'yes'}
                  onChange={(e) => setCheckFormData({ ...checkFormData, eligible_for_rehire: e.target.value as any })}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                  <FormControlLabel value="with_conditions" control={<Radio />} label="With Conditions" />
                  <FormControlLabel value="policy_prevents" control={<Radio />} label="Policy Prevents Disclosure" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Rehire Notes"
                multiline
                rows={2}
                value={checkFormData.rehire_notes || ''}
                onChange={(e) => setCheckFormData({ ...checkFormData, rehire_notes: e.target.value })}
              />
            </Grid>

            <Grid size={12}>
              <Divider><Typography variant="caption">Performance Assessment</Typography></Divider>
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label="Strengths"
                multiline
                rows={2}
                value={checkFormData.strengths || ''}
                onChange={(e) => setCheckFormData({ ...checkFormData, strengths: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Areas for Improvement"
                multiline
                rows={2}
                value={checkFormData.areas_for_improvement || ''}
                onChange={(e) => setCheckFormData({ ...checkFormData, areas_for_improvement: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Concerns"
                multiline
                rows={2}
                placeholder="Any red flags or concerns..."
                value={checkFormData.concerns || ''}
                onChange={(e) => setCheckFormData({ ...checkFormData, concerns: e.target.value })}
              />
            </Grid>

            <Grid size={12}>
              <Divider><Typography variant="caption">Overall Assessment</Typography></Divider>
            </Grid>

            <Grid size={12}>
              <FormControl component="fieldset">
                <FormLabel>Overall Recommendation</FormLabel>
                <RadioGroup
                  row
                  value={checkFormData.overall_recommendation || ''}
                  onChange={(e) => setCheckFormData({ ...checkFormData, overall_recommendation: e.target.value as any })}
                >
                  <FormControlLabel value="positive" control={<Radio />} label="Positive" />
                  <FormControlLabel value="neutral" control={<Radio />} label="Neutral" />
                  <FormControlLabel value="negative" control={<Radio />} label="Negative" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={2}
                value={checkFormData.additional_notes || ''}
                onChange={(e) => setCheckFormData({ ...checkFormData, additional_notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleSaveCheck(true)} disabled={saving}>
            Save Draft
          </Button>
          <Button
            onClick={() => handleSaveCheck(false)}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Mark Complete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Reference</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the reference for {selectedReference?.name}?
            This will also delete any check results.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteReference} color="error" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CandidateReferences;
